import { Link } from "react-router-dom";
import { ArrowUpRight, FileText, PlayCircle } from "lucide-react";
import { Reveal, MaskedLine } from "../components/site/Reveal";
import { Seo } from "../components/site/Seo";
import { DPS_MEDIA, DPS_CLIPS } from "../lib/content";

const RESOURCES = [
  { title: "Hyrox Dallas 12-Week Program", desc: "Aug 28 – Nov 13 · full periodized plan", to: "/program" },
  { title: "Hyrox Ninja × DPS Events List", desc: "Every event on the calendar", to: "/events" },
  { title: "Full Simulation Format", desc: "How sim day runs, wave by wave", to: "/simulation-format" },
];

export default function Videos() {
  return (
    <div data-testid="videos-page" className="pt-16">
      <Seo
        title="HYROX Dallas Video Vault — DPS Training Footage | HyroxNinja"
        description="Real training footage from inside Dallas Performance Systems — sleds, ergs, carries and compromised running — plus every HyroxNinja resource: the 12-week program, event list and simulation format."
      />
      <section className="relative overflow-hidden">
        <img src={DPS_MEDIA.groupPoster} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#070707]/70 via-[#070707]/40 to-[#070707]" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-20 pb-12">
          <p className="eyebrow mb-4">Hype & Video Vault</p>
          <h1 className="font-head font-black uppercase tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-6xl mb-6">
            <MaskedLine delay={0.1}>Watch the</MaskedLine>
            <MaskedLine delay={0.25} className="text-volt">Work</MaskedLine>
          </h1>
          <p className="text-zinc-400 max-w-2xl leading-relaxed">
            Real sessions, zero staging — every frame shot inside Dallas Performance
            Systems. More footage drops after each simulation.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-16 pt-4">
        <Reveal>
          <p className="eyebrow mb-4 flex items-center gap-2"><PlayCircle size={14} /> Featured — DPS Sled Work</p>
          <div className="border border-white/10 bg-black" data-testid="featured-video-container">
            <video
              src={DPS_MEDIA.featured}
              poster={DPS_MEDIA.heroPoster}
              controls
              preload="none"
              playsInline
              aria-label="Featured DPS training clip — sled push session"
              className="w-full max-h-[70vh]"
              data-testid="featured-video-player"
            />
          </div>
        </Reveal>
      </section>

      {/* DPS CLIP GALLERY */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-16" data-testid="dps-gallery-section">
        <Reveal>
          <p className="eyebrow mb-3 flex items-center gap-2"><PlayCircle size={14} /> Dallas Performance Systems</p>
          <h2 className="font-head font-extrabold uppercase tracking-tight text-2xl sm:text-3xl mb-3">
            The DPS session tapes
          </h2>
          <p className="text-zinc-400 max-w-2xl leading-relaxed mb-10">
            Nineteen color-graded clips straight from the DPS floor. Tap any clip to play.
          </p>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {DPS_CLIPS.map((clip, i) => (
            <Reveal key={clip.id} delay={Math.min((i % 3) * 0.05, 0.15)}>
              <figure className="border border-white/10 bg-[#0c0c0c] group" data-testid={`dps-clip-${clip.id}`}>
                <div className="aspect-video bg-black">
                  <video
                    src={clip.src}
                    poster={clip.thumb}
                    controls
                    preload="none"
                    playsInline
                    aria-label={`${clip.label} — training footage from Dallas Performance Systems`}
                    className="w-full h-full object-cover"
                    data-testid={`dps-clip-video-${clip.id}`}
                  />
                </div>
                <figcaption className="px-3 py-2 font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-500 group-hover:text-volt transition-colors">
                  {clip.label}
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-24">
        <Reveal>
          <p className="eyebrow mb-3">The Archive</p>
          <h2 className="font-head font-extrabold uppercase tracking-tight text-2xl sm:text-3xl mb-10">Guides & Resources</h2>
        </Reveal>
        <div className="grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
          {RESOURCES.map((r, i) => (
            <Reveal key={r.title} delay={i * 0.05} className="h-full">
              <Link to={r.to} className="bg-[#0c0c0c] p-7 flex items-start justify-between gap-4 group h-full hover:bg-[#111] transition-colors" data-testid={`resource-card-${i}`}>
                <div>
                  <FileText size={20} className="text-volt mb-3" />
                  <h3 className="font-head font-bold uppercase text-xl leading-tight mb-1 group-hover:text-volt transition-colors">{r.title}</h3>
                  <p className="text-zinc-500 text-sm">{r.desc}</p>
                </div>
                <ArrowUpRight size={18} className="text-zinc-600 group-hover:text-volt group-hover:translate-x-1 group-hover:-translate-y-1 transition-all shrink-0" />
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
